import { SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  theme,
} from "@chakra-ui/react";
import ResponsiveContainer from "@modules/Layout/components/ResponsiveContainer";
import { useLocation } from "@reach/router";
import { navigate } from "gatsby";
import React, { FC, memo, useCallback, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { If, Then, Else } from "react-if";

export type ISearchBar = {
  searchTerm: string;
};

interface Props {}

const SearchBar: React.FC<Props> = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialSearchValue = searchParams.get("searchTerm") || "";
  const [isInputFocused, setInputFocused] = useState<Boolean>(false);

  const { register, handleSubmit, getValues } = useForm<ISearchBar>({
    defaultValues: {
      searchTerm: initialSearchValue,
    },
  });

  const handleSearch: SubmitHandler<ISearchBar> = useCallback(data => {
    const searchTerm = data.searchTerm.trim();
    if (searchTerm !== "") {
      const encodedSearchTerm = encodeURIComponent(searchTerm);
      navigate(`/search?searchTerm=${encodedSearchTerm}`);
    }
  }, []);

  return (
    <Box bg={"primary700"} px={{ base: 0, lg: 8 }}>
      <ResponsiveContainer>
        <Box px={{ base: 0, md: 4 }} py={5}>
          <Box maxW={theme.breakpoints.lg} mx="auto">
            <Box as={"form"} onSubmit={handleSubmit(handleSearch)}>
              <InputGroup
                bg={"white"}
                borderRadius={40}
                overflow={"hidden"}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
              >
                <If
                  condition={Boolean(isInputFocused) || getValues("searchTerm")}
                >
                  <Then>
                    <InputRightElement px={8} mt={1}>
                      <Button onClick={handleSubmit(handleSearch)}>
                        <SearchIcon color="blue500" boxSize={6} />
                      </Button>
                    </InputRightElement>
                  </Then>
                  <Else>
                    <InputLeftElement
                      pointerEvents="none"
                      children={<SearchIcon color="blue500" boxSize={6} />}
                      px={8}
                      mt={1}
                    />
                  </Else>
                </If>
                <Input
                  id="searchInput"
                  type="text"
                  placeholder="Search..."
                  borderRadius={40}
                  border="1px solid white"
                  pl={{
                    base:
                      Boolean(isInputFocused) || getValues("searchTerm")
                        ? 4
                        : 14,
                    md: 20,
                  }}
                  pr={10}
                  size="lg"
                  focusBorderColor="white"
                  {...register("searchTerm")}
                />
              </InputGroup>
            </Box>
          </Box>
        </Box>
      </ResponsiveContainer>
    </Box>
  );
};

export default memo(SearchBar);
